import { ContactUsButton } from "@faire/help-center-self-serve/components/ContactUsButton";
import { SelfServeSource } from "@faire/help-center-self-serve/consts";
import { getTopic } from "@faire/help-center-utils/lib/topics";
import {
  loadingStateCss,
  pageMaxWidthCss,
} from "@faire/help-center/components/Mixin";
import { SearchField } from "@faire/help-center/components/SearchField/SearchField";
import { SupportParams } from "@faire/help-center/consts";
import { useSectionStore } from "@faire/help-center/hooks/useSectionStore";
import { NAVIGATION_FROM } from "@faire/help-center/views/eventEnum";
import { useStrictLocalization } from "@faire/web--source/common/localization";
import { FaireLogo } from "@faire/web--source/slate/icons/FaireLogo";
import { Close } from "@faire/web--source/slate/icons/foundation/Close";
import { Search } from "@faire/web--source/slate/icons/foundation/Search";
import { Column, HorizontalLine, Row } from "@faire/web--source/slate/Layout";
import { Spacer, getSpacing } from "@faire/web--source/slate/spacing";
import { responsiveMediaQueries } from "@faire/web--source/slate/Theme/responsiveMediaQueries";
import { useState } from "@faire/web--source/ui/hooks/useState";
import { useViewport } from "@faire/web--source/ui/hooks/useViewport";
import { Link } from "@faire/web--source/ui/LinkAnchor";
import home from "@faire/web-api--source/routes/support";
import React from "react";
import { useParams } from "react-router-dom";
import styled from "styled-components";

import { BrandRetailerMutualLink } from "./BrandRetailerMutualLink";
import { HeaderBreadcrumbs } from "./HeaderBreadcrumbs";
import { usePath } from "./hooks/usePath";

interface IProps {
  showSearch?: boolean;
}

export const Header: React.FC<IProps> = ({ showSearch }) => {
  const { topicId } = useParams<SupportParams>();
  const { isTabletAndAbove } = useViewport();
  const { strictLocalize } = useStrictLocalization();
  const { sections } = useSectionStore();
  const {
    isBasePath,
    isBrandPath: isBrandBasePath,
    getBreadcrumbPaths,
    getCategoryId,
    isLoading,
  } = usePath();

  const [showMenu, setShowMenu] = useState<boolean>(false);

  const toggleMenu = () => {
    setShowMenu(!showMenu);
  };

  const topic = topicId
    ? getTopic(isLoading, sections, Number(topicId))
    : undefined;

  const topicsCtx = {
    topic,
    topicsLoading: isLoading,
  };

  const categoryId = getCategoryId(topicsCtx);
  const breadcrumbPaths = getBreadcrumbPaths(topicsCtx, strictLocalize);
  const isBrandPath = isBrandBasePath(topicsCtx);

  return (
    <>
      <HeaderWrapper>
        <Row align="center">
          <Link to={home()}>
            <FaireLogo />
          </Link>
          <Spacer width="5x" />
          {showSearch ? (
            <>
              {isTabletAndAbove ? (
                <SearchField
                  categoryId={categoryId}
                  mini
                  miniInputSize={360}
                  isLoading={isLoading}
                />
              ) : (
                <Column onClick={toggleMenu}>
                  {showMenu ? <Close /> : <Search />}
                </Column>
              )}
            </>
          ) : null}
        </Row>
        <ContactUsButton
          source={SelfServeSource.HOME_PAGE_HEADER}
          variant="secondary"
        />
      </HeaderWrapper>
      {showMenu ? (
        <SearchField
          categoryId={categoryId}
          isLoading={isLoading}
          autoFocus
          navigatedFrom={NAVIGATION_FROM.SEARCH_PAGE}
        />
      ) : (
        <HorizontalLine />
      )}
      {!isBasePath ? (
        <>
          <SubHeader $isLoading={isLoading}>
            <HeaderBreadcrumbs breadcrumbPaths={breadcrumbPaths} />
            <BrandRetailerMutualLink isBrandPath={isBrandPath} />
          </SubHeader>
          <HorizontalLine />
        </>
      ) : null}
    </>
  );
};

const HeaderWrapper = styled.section<{ $isLoading?: boolean }>`
  position: relative;
  height: 60px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${getSpacing(0, "2x")};

  ${pageMaxWidthCss}
  ${loadingStateCss}

  section {
    margin: 0;
  }
`;

const SubHeader = styled.div<{ $isLoading?: boolean }>`
  display: flex;
  align-items: center;
  height: 43px;
  padding: ${getSpacing(0, "3x")};

  & > :not(:first-child) {
    display: none;
  }

  ${responsiveMediaQueries.tabletAndAbove} {
    padding: ${getSpacing(0, "5x")};
    & > :not(:first-child) {
      display: initial;
    }
  }

  ${responsiveMediaQueries.desktopAndAbove} {
    padding: ${getSpacing(0, "7x")};
    & > :not(:first-child) {
      display: initial;
    }
  }
  ${pageMaxWidthCss}
  ${loadingStateCss}
`;
